// FlightLookup — container / orchestrator for the flight lookup panel.
//
// Behavior-preserving refactor (issue #240, task 9.7). This file keeps the same
// module path, the same default export `FlightLookup`, and the same public props
// `{ onFlightFound, onCancel, defaultDate, defaultDepartureAirport,
// defaultArrivalAirport }` it always had. All state, effects, and the explicit
// (button-click) search flow now live in the `useFlightSearch` hook; all markup
// lives in the `flightLookup/presentation/` memoized components. The container
// is a thin shell that wires the hook's API to the presentation in the EXACT
// render order the original monolithic component produced:
//
//   <div panel wrapper>
//     <LookupHeader onCancel={onCancel} />        // header + conditional X button
//     <div>                                        // form body wrapper (as in source)
//       <LastAddedBanner ... />
//       <FlightSearchForm ... />                   // form fields + inline ErrorBanner + lookup button
//     </div>
//     <FlightResultPanel ... />                    // result panel, after the form body
//   </div>
//
// The pure logic, hook, and presentation units are independently tested; this
// container is verified by the characterization snapshots, the contract test,
// and the parity test under `flightLookup/__tests__/`.
//
// Validates: Requirements 1.1, 1.2, 2.1, 2.2, 2.3

import React from 'react';
import useFlightSearch from './flightLookup/hooks/useFlightSearch';
import LookupHeader from './flightLookup/presentation/LookupHeader';
import LastAddedBanner from './flightLookup/presentation/LastAddedBanner';
import FlightSearchForm from './flightLookup/presentation/FlightSearchForm';
import FlightResultPanel from './flightLookup/presentation/FlightResultPanel';

/**
 * FlightLookup Component
 * Reusable component for looking up flights using FlightAware API.
 *
 * @param {Object} props
 * @param {(tripFlight: Object) => void} [props.onFlightFound] - Invoked with the
 *   normalized trip flight when the user adds the found flight.
 * @param {() => void} [props.onCancel] - When provided, the header renders a
 *   cancel (X) button wired to this callback.
 * @param {string} [props.defaultDate] - Optional initial/synced date (`YYYY-MM-DD`).
 * @param {string} [props.defaultDepartureAirport] - Optional initial/synced From airport.
 * @param {string} [props.defaultArrivalAirport] - Optional initial/synced To airport.
 */
function FlightLookup({ onFlightFound, onCancel, defaultDate, defaultDepartureAirport, defaultArrivalAirport }) {
  const search = useFlightSearch({
    onFlightFound,
    onCancel,
    defaultDate,
    defaultDepartureAirport,
    defaultArrivalAirport,
  });

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '20px',
      backgroundColor: '#f9fafb',
      marginBottom: '20px'
    }}>
      <LookupHeader onCancel={onCancel} />

      <div>
        <LastAddedBanner lastAddedFlight={search.lastAddedFlight} />
        <FlightSearchForm
          lookupForm={search.lookupForm}
          loading={search.loading}
          error={search.error}
          onLookupChange={search.handleLookupChange}
          onDateChange={search.handleDateChange}
          onLookupFlight={search.handleLookupFlight}
        />
      </div>

      {/* Flight Information Display */}
      <FlightResultPanel
        flightInfo={search.flightInfo}
        loading={search.loading}
        onAddFlight={search.handleAddFlight}
      />
    </div>
  );
}

export default FlightLookup;
